import type { AABB } from '../game/ontology';
import type { Hitbox } from './Hitbox';
import type { Hurtbox } from './Hurtbox';

// The combat resolution pass: a plain AABB overlap test between every active
// Hitbox and every eligible Hurtbox. On a hit it opens the Hurtbox's reaction
// (which plays the target's `hit` Loop). Deterministic and allocation-free —
// the same primitive the platforming uses.

/** Axis-aligned bounding-box overlap (touching edges do NOT count). */
export function aabbOverlap(a: AABB, b: AABB): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

/**
 * Resolve all active Hitboxes against all vulnerable Hurtboxes. A hit lands when:
 *   - the Hitbox is active,
 *   - factions differ (no friendly fire) and owners differ (no self-hit),
 *   - the Hurtbox is not currently invulnerable,
 *   - the Hitbox hasn't already struck that Hurtbox this activation,
 *   - and the two AABBs overlap.
 */
export function resolveCombat(hitboxes: Hitbox[], hurtboxes: Hurtbox[]): void {
  for (const hit of hitboxes) {
    if (!hit.active) continue;
    for (const hurt of hurtboxes) {
      if (hurt.ownerId === hit.ownerId) continue;
      if (hurt.faction === hit.faction) continue;
      if (!hurt.vulnerable) continue;
      if (hit.hasStruck(hurt.id)) continue;
      if (!aabbOverlap(hit.box, hurt.box)) continue;
      hit.markStruck(hurt.id);
      hurt.takeHit({ faction: hit.faction, ownerId: hit.ownerId });
    }
  }
}
